import type { AuthResult, BootstrapData, HomeworkOcrResult, PlanItemsData, SessionData } from "./types";

export type HomeworkInput = {
  childId: string;
  subject: string;
  title: string;
  total: number;
  unit: string;
  startDate: string;
  targetDate: string;
  excludedWeekdays: number[];
  excludedDates: string[];
};

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string | string[] } | null;
    const message = Array.isArray(data?.message) ? data.message[0] : data?.message;
    throw new ApiError(message ?? "请求失败，请稍后重试", response.status);
  }
  return response.json() as Promise<T>;
}

export const api = {
  health: () => request<{ status: string; service: string; time: string }>("/health"),
  session: () => request<SessionData>("/auth/session"),
  enter: () => request<SessionData>("/auth/guest", { method: "POST" }),
  register: (input: { phone: string; password: string; nickname: string; acceptedTerms: true }) =>
    request<AuthResult>("/auth/register", { method: "POST", body: JSON.stringify(input) }),
  login: (input: { phone: string; password: string }) =>
    request<SessionData>("/auth/login", { method: "POST", body: JSON.stringify(input) }),
  recover: (input: { phone: string; recoveryCode: string; newPassword: string }) =>
    request<AuthResult>("/auth/recover", { method: "POST", body: JSON.stringify(input) }),
  logout: () => request<{ success: true }>("/auth/logout", { method: "POST" }),
  logoutAll: () => request<{ success: true }>("/auth/logout-all", { method: "POST" }),
  updateAccount: (input: { nickname: string }) =>
    request("/account/profile", { method: "PATCH", body: JSON.stringify(input) }),
  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    request<AuthResult>("/account/password", { method: "PATCH", body: JSON.stringify(input) }),
  deleteAccount: (input: { password: string }) =>
    request<{ success: true }>("/account", { method: "DELETE", body: JSON.stringify(input) }),
  bootstrap: () => request<BootstrapData>("/bootstrap"),
  planItems: (date: string) => request<PlanItemsData>(`/plan-items?date=${encodeURIComponent(date)}`),
  createChild: (input: { nickname: string; grade?: string }) =>
    request("/children", { method: "POST", body: JSON.stringify(input) }),
  updateChild: (id: string, input: { nickname: string; grade: string }) =>
    request(`/children/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteChild: (id: string) => request<{ success: true }>(`/children/${id}`, { method: "DELETE" }),
  recognizeHomework: (image: string) =>
    request<HomeworkOcrResult>("/ocr/homework", { method: "POST", body: JSON.stringify({ image }) }),
  createHomework: (input: HomeworkInput) =>
    request<{ id: string }>("/homeworks", { method: "POST", body: JSON.stringify(input) }),
  createHomeworks: (items: HomeworkInput[]) =>
    request<{ count: number; homeworks: Array<{ id: string }> }>("/homeworks/batch", { method: "POST", body: JSON.stringify({ items }) }),
  updateHomework: (id: string, input: HomeworkInput) =>
    request<{ id: string; planRegenerated: boolean }>(`/homeworks/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteHomework: (id: string) => request<{ success: true }>(`/homeworks/${id}`, { method: "DELETE" }),
  generatePlan: (homeworkId: string) =>
    request<{ id: string; items: Array<{ id: string; planDate: string; plannedAmount: string }> }>(
      `/homeworks/${homeworkId}/generate-plan`,
      { method: "POST" },
    ),
  toggleItem: (itemId: string, operationId: string) =>
    request(`/plan-items/${itemId}/toggle`, { method: "POST", body: JSON.stringify({ operationId }) }),
};
